import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import Spinner from "~/components-shared/Spinner";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import { genericColor, genericColorCombination } from "~/lib/resolvers";
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
  position: relative;
  z-index: 1;
  background-color: ${_ref2 => {
  let {
    $color,
    theme
  } = _ref2;
  return genericColor(theme, $color);
}};
  ${_ref3 => {
  let {
    $width
  } = _ref3;
  return $width && `width: ${$width}; min-width: ${$width}`;
}};
  ${_ref4 => {
  let {
    $border,
    theme
  } = _ref4;
  return $border && `border: 1px solid ${theme.colors.borders.primary}`;
}};
  ${_ref5 => {
  let {
    $fullWidth,
    $maxWidth
  } = _ref5;
  return $fullWidth && ($maxWidth === "initial" ? "width: 100%" : "width: 100vw");
}};
  ${_ref6 => {
  let {
    $fullHeight,
    $maxHeight
  } = _ref6;
  return $fullHeight && ($maxHeight === "initial" ? "height: 100%" : "height: 100vh");
}};
  ${_ref7 => {
  let {
    $align
  } = _ref7;
  return $align && `text-align: ${$align}`;
}};
  ${_ref8 => {
  let {
    $outline,
    $color,
    theme
  } = _ref8;
  return $outline && theme.shadows.outline(theme, $outline, genericColorCombination(theme, $color).focus);
}}
  ${_ref9 => {
  let {
    $maxWidth
  } = _ref9;
  return $maxWidth && `max-width: ${$maxWidth}`;
}};
  ${_ref10 => {
  let {
    $minHeight
  } = _ref10;
  return $minHeight && `min-height: ${$minHeight}`;
}};
  ${_ref11 => {
  let {
    $maxHeight
  } = _ref11;
  return $maxHeight && `max-height: ${$maxHeight}`;
}};
  ${_ref12 => {
  let {
    $s,
    theme
  } = _ref12;
  return theme.spacing.resolve(theme, $s);
}};
  ${_ref13 => {
  let {
    $shadow,
    theme
  } = _ref13;
  return $shadow && css`
      &:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transition: all 200ms ease-out;
        border-radius: ${theme.shape.borderRadius.primary};
        box-shadow: ${theme.shadows.middle};
        z-index: -1;
      }
    `;
}}
  ${_ref14 => {
  let {
    $hover,
    theme
  } = _ref14;
  return $hover && css`
      cursor: pointer;
      transition: all 200ms ease-out;
      backface-visibility: hidden;

      &:hover {
        scale: 1.01;
      }
      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border-radius: ${theme.shape.borderRadius.primary};
        background-color: transparent;
        z-index: 0;
        transition: background-color 200ms ease-out;
      }

      &:hover:before {
        box-shadow: ${theme.shadows.large};
      }
    `;
}}
`;
const Content = styled.div`
  transition: max-height 200ms ease-out;
  flex-grow: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: ${_ref15 => {
  let {
    $top,
    $collapsed,
    theme
  } = _ref15;
  return $collapsed ? 0 : theme.spacing.step * $top;
}}px;
  ${_ref16 => {
  let {
    $collapsed
  } = _ref16;
  return $collapsed ? `display:none; max-height: 0` : `max-height: auto`;
}};
  ${_ref17 => {
  let {
    $align
  } = _ref17;
  return $align === "stretch" && `align-items: stretch`;
}}
  ${_ref18 => {
  let {
    $align
  } = _ref18;
  return $align === "left" && `align-items: flex-start`;
}}
  ${_ref19 => {
  let {
    $align
  } = _ref19;
  return $align === "center" && `align-items: center`;
}}
  ${_ref20 => {
  let {
    $align
  } = _ref20;
  return $align === "right" && `align-items: flex-end`;
}}
  ${_ref21 => {
  let {
    $loading
  } = _ref21;
  return $loading && css`
      cursor: pointer;

      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${_ref22 => {
    let {
      theme
    } = _ref22;
    return theme.colors.background.primary;
  }};
        opacity: 0.6;
        z-index: 1;
        transition: background-color 200ms ease-out;
      }
    `;
}}
`;
const StyledSpinner = styled(Spinner)`
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;
const Title = styled.div`
  display: flex;
  align-items: center;
`;
const Span = styled.span`
  flex-grow: 1;
  color: inherit;
  display: inline-block;
`;
const Card = _ref23 => {
  let {
    title,
    elemBefore,
    elemAfter,
    collapsed = false,
    shadow = false,
    border = false,
    fullWidth = false,
    fullHeight = false,
    loading = false,
    outline = false,
    maxWidth = "initial",
    minHeight = "initial",
    maxHeight = "initial",
    color = "transparent",
    width,
    align = "stretch",
    onClick,
    children,
    s,
    ...rest
  } = _ref23;
  const {
    colors
  } = useTheme();
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    $s: {
      p: 4,
      ...s
    },
    $collapsed: collapsed,
    $fullWidth: fullWidth,
    $fullHeight: fullHeight,
    $maxWidth: maxWidth,
    $maxHeight: maxHeight,
    $minHeight: minHeight,
    $width: width,
    $shadow: shadow,
    $border: border,
    $outline: outline,
    $align: align,
    $color: color,
    $hover: typeof onClick === "function",
    onClick: onClick
  }, rest), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, (elemBefore || title || elemAfter) && /*#__PURE__*/React.createElement(Title, null, elemBefore && elemBefore, /*#__PURE__*/React.createElement(Span, null, title && title), elemAfter && elemAfter), /*#__PURE__*/React.createElement(Content, {
    $top: title ? 4 : 0,
    $collapsed: collapsed,
    $align: align,
    $loading: loading,
    $color: color || colors.background.primary
  }, children, loading && /*#__PURE__*/React.createElement(StyledSpinner, {
    center: true
  }))));
};
export default Card;